import { useState, useEffect, useMemo } from 'react';
import { useOutletContext, Link, Navigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from 'chart.js';
import { LoadingState, ErrorState, EmptyState } from '../components/States';
import { filterByDateRange, exportToCsv, exportTableToPdf } from '../utils/export';
import { useToast } from '../components/ToastProvider';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement);

const ADMIN_UIDS = ['cXXmwFwOebRnN0h6rPb1E4ASRIt1', 'WosYCVaIbrXGFLST7sjEQc1WQaU2'];

const CARD_THEME = {
    blue: { wrapper: 'bg-blue-50 border-blue-100', icon: 'bg-blue-100 text-blue-700' },
    emerald: { wrapper: 'bg-emerald-50 border-emerald-100', icon: 'bg-emerald-100 text-emerald-700' },
    sky: { wrapper: 'bg-sky-50 border-sky-100', icon: 'bg-sky-100 text-sky-700' },
    amber: { wrapper: 'bg-amber-50 border-amber-100', icon: 'bg-amber-100 text-amber-700' },
    rose: { wrapper: 'bg-rose-50 border-rose-100', icon: 'bg-rose-100 text-rose-700' },
    red: { wrapper: 'bg-red-50 border-red-100', icon: 'bg-red-100 text-red-700' },
};

const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { mode: 'index', intersect: false } },
    scales: {
        y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: 'rgba(148, 163, 184, 0.2)' } },
        x: { grid: { display: false } },
    },
};

const horizontalBarOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
        x: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: 'rgba(148, 163, 184, 0.2)' } },
        y: { grid: { display: false } },
    },
};

const simpleBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
        y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: 'rgba(148, 163, 184, 0.2)' } },
        x: { grid: { display: false } },
    },
};

const StatCard = ({ title, value, icon, theme = 'blue' }) => {
    const selectedTheme = CARD_THEME[theme] || CARD_THEME.blue;
    return (
        <div className={`rounded-2xl border p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${selectedTheme.wrapper}`}>
            <div className="flex items-center gap-4">
                <div className={`rounded-xl p-3 ${selectedTheme.icon}`}>{icon}</div>
                <div>
                    <p className="text-sm font-medium text-slate-600">{title}</p>
                    <p className="text-2xl font-bold text-slate-800">{Number(value || 0).toLocaleString()}</p>
                </div>
            </div>
        </div>
    );
};

const Panel = ({ title, subtitle, children, rightContent }) => (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-start justify-between gap-3">
            <div>
                <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
                {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
            </div>
            {rightContent}
        </div>
        {children}
    </section>
);

const getDeviceType = (ua) => {
    const lowerUa = ua?.toLowerCase() || '';
    if (/mobile|android|iphone|ipad|ipod/.test(lowerUa)) return 'มือถือ';
    return 'เดสก์ท็อป';
};

const getReferrerHostname = (referrer) => {
    if (!referrer || referrer === 'Direct' || referrer === '') return 'Direct';
    try {
        return new URL(referrer).hostname.replace('www.', '');
    } catch (_) {
        return 'Invalid';
    }
};

const AdminReport = () => {
    const { user } = useOutletContext();
    const { showToast } = useToast();

    const [rawUrls, setRawUrls] = useState([]);
    const [stats, setStats] = useState({ totalLinks: 0, totalClicks: 0, totalUsers: 0, passwordLinks: 0, customLinks: 0, disabledLinks: 0 });
    const [linksByDayData, setLinksByDayData] = useState(null);
    const [topCreatorsData, setTopCreatorsData] = useState(null);
    const [topClickedLinks, setTopClickedLinks] = useState([]);
    const [deviceData, setDeviceData] = useState(null);
    const [referrerData, setReferrerData] = useState(null);

    const [loading, setLoading] = useState(true);
    const [isDetailedLoading, setIsDetailedLoading] = useState(false);
    const [error, setError] = useState('');
    const [detailedError, setDetailedError] = useState('');
    const [isAuthorized, setIsAuthorized] = useState(null);
    const [detailedDataFetched, setDetailedDataFetched] = useState(false);

    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [creatorFilter, setCreatorFilter] = useState('');
    const [tagFilter, setTagFilter] = useState('');
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        if (user && user.uid) setIsAuthorized(ADMIN_UIDS.includes(user.uid));
        else if (user) setIsAuthorized(null);
    }, [user]);

    useEffect(() => {
        if (isAuthorized === false) {
            setLoading(false);
            return;
        }

        if (isAuthorized !== true) return;

        const fetchPrimaryData = async () => {
            setLoading(true);
            setError('');
            try {
                const urlsQuery = query(collection(db, 'urls'), orderBy('createdAt', 'desc'));
                const urlsSnapshot = await getDocs(urlsQuery);
                setRawUrls(urlsSnapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })));
            } catch (err) {
                console.error('Error fetching primary report data:', err);
                setError('เกิดข้อผิดพลาดในการดึงข้อมูลรายงานหลัก');
            } finally {
                setLoading(false);
            }
        };

        fetchPrimaryData();
    }, [isAuthorized]);

    const filteredUrls = useMemo(() => {
        const dateFiltered = filterByDateRange(rawUrls, (url) => url.createdAt, dateFrom, dateTo);
        return dateFiltered.filter((url) => {
            const creator = (url.creatorEmail || '').toLowerCase();
            const tags = (url.tags || []).map((tag) => String(tag).toLowerCase());
            const longUrl = (url.longUrl || '').toLowerCase();
            const shortCode = (url.shortCode || '').toLowerCase();

            if (creatorFilter && !creator.includes(creatorFilter.toLowerCase())) return false;
            if (tagFilter && !tags.some((tag) => tag.includes(tagFilter.toLowerCase()))) return false;

            if (searchText) {
                const q = searchText.toLowerCase();
                const inCreator = creator.includes(q);
                const inTags = tags.some((tag) => tag.includes(q));
                const inLong = longUrl.includes(q);
                const inShort = shortCode.includes(q);
                if (!inCreator && !inTags && !inLong && !inShort) return false;
            }

            return true;
        });
    }, [rawUrls, dateFrom, dateTo, creatorFilter, tagFilter, searchText]);

    useEffect(() => {
        const totalLinks = filteredUrls.length;
        const totalClicks = filteredUrls.reduce((sum, url) => sum + (url.clickCount || 0), 0);
        const totalUsers = new Set(filteredUrls.map((url) => url.uid).filter(Boolean)).size;
        const passwordLinks = filteredUrls.filter((url) => Boolean(url.password)).length;
        const customLinks = filteredUrls.filter((url) => (url.shortCode || '').length !== 6).length;
        const disabledLinks = filteredUrls.filter((url) => {
            const isExpired = url.expiresAt && url.expiresAt.toDate() < new Date();
            const maxReached = url.maxClicks > 0 && (url.clickCount || 0) >= url.maxClicks;
            return isExpired || maxReached;
        }).length;
        setStats({ totalLinks, totalClicks, totalUsers, passwordLinks, customLinks, disabledLinks });

        const sortedByClicks = [...filteredUrls].sort((a, b) => (b.clickCount || 0) - (a.clickCount || 0));
        setTopClickedLinks(sortedByClicks.slice(0, 10));

        const linksByDay = filteredUrls.reduce((acc, url) => {
            if (url.createdAt) {
                const day = url.createdAt.toDate().toLocaleDateString('en-CA');
                acc[day] = (acc[day] || 0) + 1;
            }
            return acc;
        }, {});
        const sortedDays = Object.keys(linksByDay).sort();
        setLinksByDayData({
            labels: sortedDays.map((day) => new Date(day).toLocaleDateString('th-TH', { month: 'short', day: 'numeric' })),
            datasets: [
                {
                    label: 'ลิงก์ที่สร้าง',
                    data: sortedDays.map((day) => linksByDay[day]),
                    borderColor: '#2563EB',
                    backgroundColor: 'rgba(37, 99, 235, 0.16)',
                    pointBackgroundColor: '#1D4ED8',
                    pointRadius: 3,
                    borderWidth: 2,
                    fill: true,
                    tension: 0.35,
                },
            ],
        });

        const creators = filteredUrls.reduce((acc, url) => {
            const email = url.creatorEmail || 'ไม่ระบุ';
            acc[email] = (acc[email] || 0) + 1;
            return acc;
        }, {});

        const sortedCreators = Object.entries(creators).sort(([, a], [, b]) => b - a).slice(0, 10);
        setTopCreatorsData({
            labels: sortedCreators.map((creator) => creator[0]),
            datasets: [
                {
                    label: 'จำนวนลิงก์',
                    data: sortedCreators.map((creator) => creator[1]),
                    backgroundColor: 'rgba(13, 148, 136, 0.7)',
                    borderColor: 'rgba(15, 118, 110, 1)',
                    borderWidth: 1,
                    borderRadius: 8,
                },
            ],
        });
    }, [filteredUrls]);

    const handleExportCsv = () => {
        exportToCsv(
            'admin-report.csv',
            ['Short Code', 'Long URL', 'Creator Email', 'Clicks', 'Tags', 'Created At'],
            filteredUrls.map((url) => [
                url.shortCode || '',
                url.longUrl || '',
                url.creatorEmail || 'ไม่ระบุ',
                url.clickCount || 0,
                (url.tags || []).join(', '),
                url.createdAt ? new Date(url.createdAt.toDate()).toLocaleString('th-TH') : '-',
            ])
        );
        showToast('ส่งออก CSV สำเร็จ', 'success');
    };

    const handleExportPdf = () => {
        const ok = exportTableToPdf({
            filename: 'admin-report.pdf',
            title: '\u0e23\u0e32\u0e22\u0e07\u0e32\u0e19\u0e20\u0e32\u0e1e\u0e23\u0e27\u0e21\u0e23\u0e30\u0e1a\u0e1a',
            subtitle: `\u0e08\u0e33\u0e19\u0e27\u0e19\u0e23\u0e32\u0e22\u0e01\u0e32\u0e23 ${filteredUrls.length} \u0e25\u0e34\u0e07\u0e01\u0e4c`,
            columns: ['Short Code', 'Long URL', 'Creator', 'Clicks', 'Tags', 'Created At'],
            rows: filteredUrls.map((url) => [
                url.shortCode || '-',
                url.longUrl || '-',
                url.creatorEmail || '\u0e44\u0e21\u0e48\u0e23\u0e30\u0e1a\u0e38',
                url.clickCount || 0,
                (url.tags || []).join(', '),
                url.createdAt ? new Date(url.createdAt.toDate()).toLocaleString('th-TH') : '-',
            ]),
        });
        if (ok) showToast('\u0e2a\u0e48\u0e07\u0e2d\u0e2d\u0e01 PDF \u0e2a\u0e33\u0e40\u0e23\u0e47\u0e08', 'success');
        else showToast('\u0e44\u0e21\u0e48\u0e2a\u0e32\u0e21\u0e32\u0e23\u0e16\u0e2a\u0e48\u0e07\u0e2d\u0e2d\u0e01 PDF \u0e44\u0e14\u0e49', 'error');
    };

    const handleFetchDetailedData = async () => {
        setIsDetailedLoading(true);
        setDetailedError('');
        setDetailedDataFetched(true);

        try {
            const linkToAnalyze = topClickedLinks[0];
            if (!linkToAnalyze) {
                setDetailedError('ยังไม่พบข้อมูลลิงก์ยอดนิยมสำหรับวิเคราะห์');
                setIsDetailedLoading(false);
                return;
            }

            const clicksSnapshot = await getDocs(collection(db, 'urls', linkToAnalyze.id, 'clicks'));
            const allClicks = clicksSnapshot.docs.map((docItem) => docItem.data());

            if (allClicks.length === 0) {
                setDetailedError(`ยังไม่พบข้อมูลการคลิกสำหรับลิงก์: ${linkToAnalyze.shortCode}`);
                setIsDetailedLoading(false);
                return;
            }

            const devices = allClicks.reduce((acc, click) => {
                const type = getDeviceType(click.userAgent);
                acc[type] = (acc[type] || 0) + 1;
                return acc;
            }, {});
            setDeviceData({
                labels: Object.keys(devices),
                datasets: [{ data: Object.values(devices), backgroundColor: ['#3B82F6', '#10B981'], hoverBackgroundColor: ['#2563EB', '#059669'] }],
            });

            const referrers = allClicks.reduce((acc, click) => {
                const hostname = getReferrerHostname(click.referrer);
                acc[hostname] = (acc[hostname] || 0) + 1;
                return acc;
            }, {});
            const sortedReferrers = Object.entries(referrers).sort(([, a], [, b]) => b - a).slice(0, 5);
            setReferrerData({
                labels: sortedReferrers.map((referrer) => referrer[0]),
                datasets: [{ label: 'จำนวนคลิก', data: sortedReferrers.map((referrer) => referrer[1]), backgroundColor: 'rgba(14, 165, 233, 0.75)' }],
            });
        } catch (clickError) {
            console.error('Could not fetch detailed click data:', clickError);
            setDetailedError('ไม่สามารถโหลดข้อมูลเชิงลึกได้ อาจเกิดจากข้อจำกัดของฐานข้อมูล');
        } finally {
            setIsDetailedLoading(false);
        }
    };

    if (isAuthorized === null) return <LoadingState message="กำลังตรวจสอบสิทธิ์..." />;
    if (isAuthorized === false) return <Navigate to="/" replace />;
    if (loading) return <LoadingState message="กำลังโหลดรายงาน..." />;
    if (error) return <ErrorState message={error} />;

    return (
        <main className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-sky-50/40">
            <div className="container mx-auto p-4 sm:p-6 lg:p-8">
                <div className="mb-5 flex items-center justify-between">
                    <Link to="/" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50">
                        กลับไปหน้าแดชบอร์ด
                    </Link>
                </div>

                <section className="mb-6 rounded-3xl border border-slate-200 bg-white/95 p-6 shadow-sm backdrop-blur sm:p-8">
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                        <div>
                            <p className="mb-2 inline-flex rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-sky-700">Admin Report</p>
                            <h1 className="text-3xl font-extrabold tracking-tight text-slate-800 sm:text-4xl">รายงานภาพรวมระบบ</h1>
                            <p className="mt-2 max-w-2xl text-sm text-slate-600 sm:text-base">สรุปผลการใช้งานลิงก์ทั้งหมด พร้อมตัวกรองเชิงลึกเพื่อวิเคราะห์ได้แม่นยำขึ้น</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button onClick={handleExportCsv} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium">ส่งออก CSV</button>
                            <button onClick={handleExportPdf} className="rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white">ส่งออก PDF</button>
                        </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-5">
                        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-md border border-slate-300 px-2 py-2 text-sm" />
                        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-md border border-slate-300 px-2 py-2 text-sm" />
                        <input type="text" value={creatorFilter} onChange={(e) => setCreatorFilter(e.target.value)} placeholder="กรองผู้สร้าง" className="rounded-md border border-slate-300 px-2 py-2 text-sm" />
                        <input type="text" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} placeholder="กรองแท็ก" className="rounded-md border border-slate-300 px-2 py-2 text-sm" />
                        <input type="text" value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="ค้นหา URL/โค้ด/ผู้สร้าง" className="rounded-md border border-slate-300 px-2 py-2 text-sm" />
                    </div>
                </section>

                <section className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
                    <StatCard title="ลิงก์ทั้งหมด" value={stats.totalLinks} icon={<LinkIcon />} theme="blue" />
                    <StatCard title="คลิกทั้งหมด" value={stats.totalClicks} icon={<CursorClickIcon />} theme="emerald" />
                    <StatCard title="ผู้ใช้งาน" value={stats.totalUsers} icon={<UsersIcon />} theme="sky" />
                    <StatCard title="ลิงก์มีรหัสผ่าน" value={stats.passwordLinks} icon={<LockClosedIcon />} theme="amber" />
                    <StatCard title="ลิงก์กำหนดเอง" value={stats.customLinks} icon={<AtSymbolIcon />} theme="rose" />
                    <StatCard title="ลิงก์ไม่ทำงาน" value={stats.disabledLinks} icon={<BanIcon />} theme="red" />
                </section>

                {filteredUrls.length === 0 ? (
                    <EmptyState title="ไม่พบข้อมูลตามเงื่อนไขที่กรอง" description="ลองปรับช่วงวันที่หรือคำค้นหาใหม่" />
                ) : (
                    <section className="grid grid-cols-1 gap-6 xl:grid-cols-12">
                        <div className="space-y-6 xl:col-span-8">
                            <Panel title="จำนวนลิงก์ที่สร้างรายวัน" subtitle="ดูแนวโน้มการเติบโตของการสร้างลิงก์ในแต่ละวัน">
                                <div className="h-[280px] sm:h-[320px]">{linksByDayData ? <Line data={linksByDayData} options={lineChartOptions} /> : <EmptyState />}</div>
                            </Panel>

                            <Panel title="ลิงก์ที่มีการคลิกสูงสุด (Top 10)" subtitle="จัดอันดับตามจำนวนคลิกจากมากไปน้อย">
                                <div className="overflow-x-auto">
                                    <table className="w-full min-w-[640px] text-left text-sm">
                                        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                                            <tr>
                                                <th className="px-4 py-3">ลิงก์ย่อ</th>
                                                <th className="px-4 py-3">ผู้สร้าง</th>
                                                <th className="px-4 py-3 text-center">จำนวนคลิก</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {topClickedLinks.map((link, index) => (
                                                <tr key={link.shortCode || link.id} className="border-b border-slate-100 hover:bg-slate-50/70">
                                                    <td className="px-4 py-3 font-medium text-slate-800">
                                                        <div className="flex items-center gap-2">
                                                            <span className="inline-flex h-6 min-w-6 items-center justify-center rounded bg-blue-100 px-1.5 text-xs font-bold text-blue-700">{index + 1}</span>
                                                            <a href={`/${link.shortCode}`} target="_blank" rel="noopener noreferrer" className="max-w-[260px] truncate text-blue-700 hover:underline" title={link.longUrl}>
                                                                {window.location.host}/{link.shortCode}
                                                            </a>
                                                        </div>
                                                    </td>
                                                    <td className="max-w-[220px] truncate px-4 py-3 text-slate-600" title={link.creatorEmail}>{link.creatorEmail || 'ไม่ระบุ'}</td>
                                                    <td className="px-4 py-3 text-center text-base font-bold text-slate-800">{(link.clickCount || 0).toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Panel>
                        </div>

                        <div className="space-y-6 xl:col-span-4">
                            <Panel
                                title="ข้อมูลเชิงลึก"
                                subtitle="วิเคราะห์จากลิงก์ที่มียอดคลิกสูงสุด"
                                rightContent={
                                    !detailedDataFetched ? (
                                        <button onClick={handleFetchDetailedData} className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700 disabled:opacity-60" disabled={isDetailedLoading}>
                                            {isDetailedLoading ? 'กำลังวิเคราะห์...' : 'เริ่มวิเคราะห์'}
                                        </button>
                                    ) : null
                                }
                            >
                                {!detailedDataFetched ? (
                                    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">กดปุ่ม "เริ่มวิเคราะห์" เพื่อโหลดข้อมูลประเภทอุปกรณ์และแหล่งที่มาของการคลิก</div>
                                ) : (
                                    <div className="space-y-6">
                                        {detailedError ? <p className="text-sm text-red-600">{detailedError}</p> : null}
                                        <div>
                                            <h4 className="mb-2 text-sm font-semibold text-slate-700">ประเภทอุปกรณ์</h4>
                                            <div className="h-[220px]">{deviceData ? <Doughnut data={deviceData} /> : <EmptyState />}</div>
                                        </div>
                                        <div>
                                            <h4 className="mb-2 text-sm font-semibold text-slate-700">แหล่งที่มา (Top 5)</h4>
                                            <div className="h-[220px]">{referrerData ? <Bar data={referrerData} options={horizontalBarOptions} /> : <EmptyState />}</div>
                                        </div>
                                    </div>
                                )}
                            </Panel>

                            <Panel title="ผู้สร้างลิงก์สูงสุด (Top 10)" subtitle="แสดงผู้ใช้งานที่สร้างลิงก์มากที่สุดในระบบ">
                                <div className="h-[300px]">{topCreatorsData ? <Bar data={topCreatorsData} options={simpleBarOptions} /> : <EmptyState />}</div>
                            </Panel>
                        </div>
                    </section>
                )}
            </div>
        </main>
    );
};

const LinkIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.536a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>;
const CursorClickIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m1.586 5.854l-2.122 2.122M15 15l-2 5L9 9l11 4-5 2z" /></svg>;
const UsersIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M15 21a6 6 0 00-9-5.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-3-5.197M15 21a9 9 0 00-9-9" /></svg>;
const LockClosedIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>;
const AtSymbolIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" /></svg>;
const BanIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>;

export default AdminReport;
